import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Table, Tag, Spin, message, Tooltip, Button, Select } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../api';

const STATUS_COLOR = { Submitted: 'processing', Acknowledged: 'success' };

const SHORT_REASON = {
  'Sick/Health Related': 'Sick',
  'Jury Duty':           'Jury Duty',
  'Bereavement':         'Bereavement',
  'Outage':              'Outage',
  'Other':               'Other',
};

function fmtTime(t) {
  if (!t) return '';
  const m = t.match(/^(\d+):(\d+)\s*(AM|PM)$/i);
  if (!m) return t;
  const [, h, min, period] = m;
  return min === '00'
    ? `${h}${period.toLowerCase()}`
    : `${h}:${min}${period.toLowerCase()}`;
}

// Hours from 'hh:mm AM/PM' start to end; an end at or before the start runs
// past midnight; 0 when either is missing. The scorecards' rule.
function absenceHours(a) {
  const mins = (t) => {
    const m = /^(\d{1,2}):(\d{2})\s*([AP]M)$/i.exec((t || '').trim());
    return m ? ((+m[1] % 12) + (m[3].toUpperCase() === 'PM' ? 12 : 0)) * 60 + +m[2] : null;
  };
  const st = mins(a.StartTime); const en = mins(a.EndTime);
  if (st == null || en == null) return 0;
  return ((en - st <= 0 ? en - st + 1440 : en - st)) / 60;
}

function CellContent({ items }) {
  if (!items?.length) return null;
  return items.map((a, i) => {
    const label    = SHORT_REASON[a.Reason] || a.Reason;
    // Full or Partial, and the times: every absence now carries a start and
    // end time, Entire Day included.
    const isPartial = a.AbsencePeriod === 'Absent Part of Day';
    const kind     = isPartial ? 'Partial' : 'Full';
    const timeStr  = a.StartTime
      ? `${fmtTime(a.StartTime)}–${fmtTime(a.EndTime)}`
      : null;
    const tip = `${a.Reason} · ${isPartial ? 'Partial Day' : 'Full Day'}${timeStr ? ` · ${timeStr}` : ''} · ${a.Status}`;
    return (
      <Tooltip key={i} title={tip}>
        <Tag
          color={STATUS_COLOR[a.Status] || 'default'}
          style={{ fontSize: 11, lineHeight: '16px', padding: '1px 5px', display: 'block', marginBottom: 2, cursor: 'default' }}
        >
          {label} · {kind}
          {timeStr && (
            <div style={{ fontSize: 10, fontWeight: 400, marginTop: 1 }}>{timeStr}</div>
          )}
        </Tag>
      </Tooltip>
    );
  });
}

export default function UpcomingAbsencePivot({ teamFilter = [], setTeamFilter, teamOptions = [], lockTeam = false }) {
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading]   = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (teamFilter.length) params.team_id = teamFilter.join(',');
    api.get('/api/absences/upcoming', { params })
      .then((res) => setAbsences(res.data))
      .catch(() => message.error('Failed to load upcoming absences.'))
      .finally(() => setLoading(false));
  }, [teamFilter]);

  useEffect(() => { load(); }, [load]);

  const today = dayjs().startOf('day');
  const todayStr = today.format('YYYY-MM-DD');
  const dates = Array.from({ length: 14 }, (_, i) => today.add(i, 'day').format('YYYY-MM-DD'));

  // Build: teamMap[teamName][userID] = { UserName, [date]: [absence, ...] }
  const teamMap = {};
  absences.forEach((a) => {
    const team = a.TeamName || '(No Team)';
    if (!teamMap[team]) teamMap[team] = {};
    const uid = String(a.UserID);
    if (!teamMap[team][uid]) teamMap[team][uid] = { _key: uid, UserName: a.UserName };

    const start = dayjs(a.DateStart);
    const end   = dayjs(a.DateEnd || a.DateStart);
    let cur = start;
    while (!cur.isAfter(end)) {
      const d = cur.format('YYYY-MM-DD');
      // only include dates within our 14-day window
      if (d >= todayStr && d <= dates[dates.length - 1]) {
        if (!teamMap[team][uid][d]) teamMap[team][uid][d] = [];
        teamMap[team][uid][d].push(a);
      }
      cur = cur.add(1, 'day');
    }
  });

  const sortedTeams = Object.keys(teamMap).sort((a, b) => {
    if (a === '(No Team)') return 1;
    if (b === '(No Team)') return -1;
    return a.localeCompare(b);
  });

  const dateColumns = dates.map((d) => {
    const dj        = dayjs(d);
    const isWeekend = dj.day() === 0 || dj.day() === 6;
    const isToday   = d === todayStr;
    return {
      title: (
        <div style={{ textAlign: 'center', fontSize: 11, lineHeight: '15px' }}>
          <div style={{ fontWeight: isToday ? 700 : 400, color: isToday ? '#1677ff' : undefined }}>
            {dj.format('ddd')}
          </div>
          <div>{dj.format('M/D')}</div>
        </div>
      ),
      key:   d,
      width: 90,
      align: 'center',
      onHeaderCell: () => ({ style: { background: isWeekend ? '#f9f9f9' : undefined, padding: '4px 2px' } }),
      onCell:       () => ({ style: { background: isWeekend ? '#f9f9f9' : undefined, padding: '4px 6px', verticalAlign: 'top' } }),
      render: (_, record) => <CellContent items={record[d]} />,
    };
  });

  const columns = [
    {
      title:     'Employee',
      dataIndex: 'UserName',
      key:       'UserName',
      fixed:     'left',
      width:     150,
      ellipsis:  true,
    },
    ...dateColumns,
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 60 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        {!lockTeam && (
          <Select
            mode="multiple" allowClear showSearch optionFilterProp="label"
            placeholder="All teams"
            options={teamOptions}
            value={teamFilter}
            onChange={setTeamFilter}
            maxTagCount="responsive"
            style={{ minWidth: 200, maxWidth: 400 }}
          />
        )}
        <Button size="small" icon={<ReloadOutlined />} onClick={load}>Refresh</Button>
      </div>

      {sortedTeams.length === 0 && (
        <Typography.Text type="secondary">No upcoming absences in the next 14 days.</Typography.Text>
      )}

      {sortedTeams.map((team) => {
        const rows = Object.values(teamMap[team])
          .sort((a, b) => a.UserName.localeCompare(b.UserName));
        return (
          <div key={team} style={{ marginBottom: 28 }}>
            <Typography.Title level={5} style={{ marginBottom: 6, color: '#444' }}>
              {team}
            </Typography.Title>
            <Table
              rowKey="_key"
              dataSource={rows}
              columns={columns}
              pagination={false}
              scroll={{ x: 'max-content' }}
              size="small"
              bordered
              // Total Hrs: each day's absences on the team, added up.
              summary={() => (
                <Table.Summary.Row style={{ background: '#fafafa' }}>
                  <Table.Summary.Cell index={0}><strong>Total Hrs</strong></Table.Summary.Cell>
                  {dates.map((d, i) => {
                    const hrs = rows.reduce((sum, r) => sum + (r[d] || []).reduce((x, a) => x + absenceHours(a), 0), 0);
                    return (
                      <Table.Summary.Cell key={d} index={i + 1} align="center">
                        {hrs > 0 ? <strong>{hrs.toFixed(1)}</strong> : <span style={{ color: '#d9d9d9' }}>—</span>}
                      </Table.Summary.Cell>
                    );
                  })}
                </Table.Summary.Row>
              )}
            />
          </div>
        );
      })}

      {sortedTeams.length > 0 && (
        <div style={{ marginTop: 4, fontSize: 12, color: '#888' }}>
          <Tag color="processing" style={{ fontSize: 11 }}>blue</Tag> Submitted &nbsp;
          <Tag color="success"    style={{ fontSize: 11 }}>green</Tag> Acknowledged
        </div>
      )}
    </div>
  );
}
